import { RESOURCE_IMAGE_CDN_URL } from 'shared/consts';

/** Helix upload prefixes that live on the image CDN rather than the API host. */
const CDN_PREFIXES = ['uploads/', 'default_images/'];

const isAbsoluteUrl = (value: string): boolean => {
  try {
    return !!new URL(value).protocol;
  } catch {
    return false;
  }
};

/**
 * Turns a value helix stored for an uploaded image into something an `<img src>` can load.
 *
 * ⛔ HELIX HANDS BACK A STORAGE PATH, NOT A URL. `/images/upload` returns a relative
 * `uploads/<hash>_<ts>.<ext>`, and the columns that hold it (`companies.logo_url`, a floorplan's
 * `imageUrl`, …) are typed as URLs and validated with `@IsUrl({ require_tld: false })`, which lets
 * such a path straight through. So a stored value can be EITHER form, and the relative one is the
 * common case.
 *
 * ⚠️ AND RENDERING IT RAW FAILS SILENTLY. A relative `src` resolves against whatever origin the app
 * happens to be served from — an Amplify PR preview, the CDN, localhost — none of which hold the
 * file. The browser 404s, `onError` runs, and whatever fallback the component has (initials, a
 * placeholder) takes over, so the UI looks deliberate and nothing is logged. RES-1316 lost a
 * vendor's logo to exactly this: the data was correct the whole way down, and the only symptom was
 * an initials disc where a mark belonged.
 *
 * @param value   what helix stored, in either form.
 * @param apiUrl  only needed to resolve a ROOT-relative `/…` path, which is served from the API
 *                host rather than the CDN. Omit it where that form cannot occur; such a path is
 *                then returned untouched rather than guessed at.
 */
export const resolveHelixUploadUrl = (value: string | undefined | null, apiUrl?: string): string => {
  const stored = value?.trim();

  if (!stored) {
    return '';
  }

  if (isAbsoluteUrl(stored)) {
    return stored;
  }

  if (CDN_PREFIXES.some(prefix => stored.startsWith(prefix))) {
    return `${RESOURCE_IMAGE_CDN_URL}/${stored}`;
  }

  if (stored.startsWith('/') && apiUrl) {
    try {
      return `${new URL(apiUrl).origin}${stored}`;
    } catch {
      return stored;
    }
  }

  return stored;
};
